import React, { useState, useEffect } from 'react';
import {
  Button,
  DialogContent,
  Input,
  palettes,
  Select,
  useFlashMessage,
  Theme,
  SelectChangeEvent
} from '@indigo-ag/component-module';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18next from 'core/i18n';
import {
  AgFinancingFile,
  AgFinancingFilesContext,
  Serasa as ISerasa,
  StateJudicialCheck as IStateJudicialCheck,
  AgFinancingDebtType,
  AgFinancingEvaluationType,
  Phase1Input,
  AgFinancingFileInput
} from '@indigo-ag/schema';

import { currency } from 'shared/utils';
import {
  createStylesHook,
  EnhancedDialog,
  UploadDocument,
  ISerasaInfo,
  IStateJudicialCheckInfo,
  CURRENCY_SYMBOL,
  debtTypesOptions,
  evaluationTypesOptions,
  ModalActionButtons
} from 'shared';
import {
  agFinancingFilesToFiles,
  removeFilesFromAgFinancing,
  getDebtTypeTranslated,
  ProgressDictionary,
  updateFilesAgFinancingFiles,
  uploadFilesToAgFinancing
} from '../../../../utils';
import { ApolloError } from '@apollo/client';

export interface BackgroundCheckModalSerasaJuditialCheckProps {
  closeModal: () => void;
  open: boolean;
  title: string;
  serasaInfo?: ISerasaInfo;
  stateJudicialCheckInfo?: IStateJudicialCheckInfo;
  onSave: (newPhaseInfo: Partial<Phase1Input>) => Promise<unknown>;
  toUploadFiles: ReturnType<typeof uploadFilesToAgFinancing>;
}

const useStyles = createStylesHook((theme: Theme) => ({
  flexDocument: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  inputStyle: {
    marginRight: '32px !important'
  },
  size14Black: {
    fontSize: theme.typography.pxToRem(14),
    color: palettes.neutral['700']
  }
}));

export const BackgroundCheckModalSerasaJuditialCheck: React.FunctionComponent<BackgroundCheckModalSerasaJuditialCheckProps> = ({
  closeModal,
  open,
  title,
  serasaInfo,
  stateJudicialCheckInfo,
  onSave,
  toUploadFiles
}) => {
  const isSerasa = (): boolean => {
    return title.toLowerCase().includes('serasa');
  };

  const getInitialFiles = (): Partial<AgFinancingFile>[] => {
    return isSerasa()
      ? serasaInfo?.serasaFiles
        ? serasaInfo?.serasaFiles
        : []
      : stateJudicialCheckInfo?.stateJudicialCheckFiles
      ? stateJudicialCheckInfo?.stateJudicialCheckFiles
      : [];
  };

  const getInitialDebts = (): (ISerasa | IStateJudicialCheck)[] => {
    return isSerasa()
      ? serasaInfo?.serasa
        ? serasaInfo?.serasa
        : []
      : stateJudicialCheckInfo?.stateJudicialCheck
      ? stateJudicialCheckInfo?.stateJudicialCheck
      : [];
  };

  const getInitialNotes = (): string => {
    return isSerasa()
      ? serasaInfo?.serasaNotes
        ? serasaInfo?.serasaNotes
        : ''
      : stateJudicialCheckInfo?.stateJudicialCheckNotes
      ? stateJudicialCheckInfo?.stateJudicialCheckNotes
      : '';
  };

  const getInitialEvaluation = (): AgFinancingEvaluationType | null => {
    return isSerasa()
      ? serasaInfo?.serasaEvaluation
        ? serasaInfo?.serasaEvaluation
        : null
      : stateJudicialCheckInfo?.stateJudicialCheckEvaluation
      ? stateJudicialCheckInfo?.stateJudicialCheckEvaluation
      : null;
  };

  /**
   * State
   */
  const [allFiles, setAllFiles] = useState<Partial<AgFinancingFile>[]>(getInitialFiles());
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [filesIdsToRemove, setFilesIdsToRemove] = useState<AgFinancingFile['id'][]>([]);
  const [option, setOption] = useState<AgFinancingDebtType | null>(null);
  const [evaluation, setEvaluation] = useState<AgFinancingEvaluationType | null>(getInitialEvaluation());
  const [name, setName] = useState('');
  const [amount, setAmount] = useState(0);
  const [debts, setDebts] = useState<(ISerasa | IStateJudicialCheck)[]>(getInitialDebts());
  const [note, setNote] = useState(getInitialNotes());
  const [progress, setProgress] = useState<ProgressDictionary>();
  const [filesUpdated, setFilesUpdated] = useState<AgFinancingFile[]>([]);
  /**
   * Styles
   */
  const classes = useStyles();

  const onCleanState = () => {
    setAllFiles(getInitialFiles());
    setNewFiles([]);
    setFilesIdsToRemove([]);
    setOption(null);
    setName('');
    setAmount(0);
    setDebts(getInitialDebts());
    setNote(getInitialNotes());
    setEvaluation(getInitialEvaluation());
    setFilesUpdated([]);
  };

  useEffect(() => {
    onCleanState();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setNote(event.target.value);
  };

  const handleAddDebts = (): void => {
    setDebts([
      ...debts,
      {
        amount,
        name,
        type: option as AgFinancingDebtType
      }
    ]);
    setAmount(0);
    setOption(null);
    setName('');
  };

  const handleRemoveDebt = (debt: number) => {
    return () => {
      const newDebts = debts.filter((_, index) => index !== debt);
      setDebts(newDebts);
    };
  };

  const handleName = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setName(event.target.value);
  };

  const handleAmount = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setAmount(parseFloat(event.target.value));
  };

  const handleChange = (event: SelectChangeEvent<AgFinancingDebtType>): void => {
    setOption(event.target.value as AgFinancingDebtType);
  };

  const handleEvaluationChange = (event: SelectChangeEvent<AgFinancingEvaluationType>): void => {
    setEvaluation(event.target.value);
  };

  const addNewFiles = (files: File[]) => {
    setNewFiles([...newFiles, ...files]);

    setAllFiles([
      ...allFiles,
      ...files.map(f => ({
        ...f,
        documentName: f.name
      }))
    ]);
  };

  const fileReplacer = (file: File) => {
    const fileIndex = newFiles.findIndex(f => f['newFileId'] === file['newFileId']);
    const replacedFiles = [...newFiles];
    replacedFiles[fileIndex]['comment'] = file['comment'];
    setNewFiles(replacedFiles);
  };

  const onUpdateFile = (file: File) => {
    updateFilesAgFinancingFiles(file, allFiles, filesUpdated, setFilesUpdated);
  };

  const addFileToRemove = (fileIdx: number) => {
    const updatedFiles = [...allFiles];
    const fileToRemove = updatedFiles.splice(fileIdx, 1)[0];

    if (fileToRemove.id) {
      setFilesIdsToRemove([...filesIdsToRemove, fileToRemove.id]);
    } else {
      const updatedNewFiles = [...newFiles];
      const newFileToRemoveIdx =
        fileIdx -
        (isSerasa()
          ? serasaInfo?.serasaFiles
            ? serasaInfo.serasaFiles.length
            : 0
          : stateJudicialCheckInfo?.stateJudicialCheckFiles
          ? stateJudicialCheckInfo.stateJudicialCheckFiles.length
          : 0);
      updatedNewFiles.splice(newFileToRemoveIdx, 1);
      setNewFiles(updatedNewFiles);
    }

    setAllFiles(updatedFiles);
  };

  const isAddBtnDisabled = !name || !amount || !option;

  const renderBrazilCurrencySymbol = () => (
    <span style={{ color: palettes.coastal.core.mediumBlue, fontWeight: 500 }}>{CURRENCY_SYMBOL.BR}</span>
  );

  const { addFlashMessage } = useFlashMessage();

  const updatePhaseInfo = () => {
    const newPhaseInfo: Partial<Phase1Input> = {};

    const newDebts: (ISerasa | IStateJudicialCheck)[] = debts.map(d => ({
      type: d.type,
      name: d.name,
      amount: d.amount
    }));

    if (!isAddBtnDisabled) {
      newDebts.push({
        amount,
        name,
        type: option as AgFinancingDebtType
      });
    }

    if (isSerasa()) {
      newPhaseInfo.serasa = newDebts;
      newPhaseInfo.serasaNotes = note;
      newPhaseInfo.serasaEvaluation = evaluation || undefined;
    } else {
      newPhaseInfo.stateJudicialCheck = newDebts;
      newPhaseInfo.stateJudicialCheckNotes = note;
      newPhaseInfo.stateJudicialCheckEvaluation = evaluation || undefined;
    }

    return removeFilesFromAgFinancing(filesIdsToRemove)
      .then(() =>
        toUploadFiles(
          newFiles,
          isSerasa() ? AgFinancingFilesContext.SERASA : AgFinancingFilesContext.STATE_JUDICIAL_CHECK,
          undefined,
          progressCompleted => setProgress(progressCompleted)
        )
      )
      .then(filesUploaded =>
        onSave({
          ...newPhaseInfo,
          files: filesUploaded.fulfilled as AgFinancingFileInput[]
        })
      )
      .then(() => {
        addFlashMessage({
          content: `${isSerasa() ? 'Serasa' : 'State Judicial Check'} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
          title: '',
          variant: 'success'
        });
        setProgress({});
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  return (
    <EnhancedDialog open={open} onClose={closeModal} title={title}>
      <DialogContent>
        <Select
          style={{ marginBottom: 10 }}
          label={i18next.t('evaluation')}
          name="evaluation-options"
          options={evaluationTypesOptions}
          onChange={handleEvaluationChange}
          value={evaluation}
          placeholder={i18next.t('pleaseSelect')}
        />
        <Select
          style={{ marginBottom: 10 }}
          label={`${i18next.t('debtType')} (${i18next.t('optional')})`}
          name="debt"
          options={debtTypesOptions}
          onChange={handleChange}
          value={option}
          placeholder={i18next.t('pleaseSelect')}
        />
        {(option || debts.length > 0) && (
          <>
            <div className={classes.flexDocument}>
              <Input label={i18next.t('name')} className={classes.inputStyle} value={name} onChange={handleName} />
              <Input
                label={i18next.t('amount')}
                value={isNaN(amount) ? '' : String(amount)}
                onChange={handleAmount}
                renderBeforeInput={renderBrazilCurrencySymbol}
              />
            </div>
            <Button
              size="small"
              variant="secondary"
              disabled={isAddBtnDisabled}
              onClick={handleAddDebts}
              style={{ marginBottom: 24 }}
            >
              {i18next.t('add')}
            </Button>
            {debts.length > 0 && <p className={classes.size14Black}>{i18next.t('debt')}</p>}
            {debts &&
              debts.map((debt, index: number) => (
                <div key={index} className={classes.flexDocument} style={{ marginBottom: 20 }}>
                  <span style={{ marginBottom: 10 }}>
                    <FontAwesomeIcon
                      icon={faCheck}
                      size="lg"
                      style={{ color: palettes.coastal.core.mediumBlue, marginRight: 20 }}
                    />
                    {getDebtTypeTranslated(debt.type)}
                    {` ${debt.name} - ${currency.formatBrazilCurrency(debt.amount)}`}
                  </span>
                  <span
                    onClick={handleRemoveDebt(index)}
                    style={{ color: palettes.coastal.core.mediumBlue, cursor: 'pointer' }}
                  >
                    {i18next.t('remove')}
                  </span>
                </div>
              ))}
          </>
        )}
        <div style={{ marginBottom: 25 }}>
          <Input label={`${i18next.t('notes')} (${i18next.t('optional')})`} value={note} onChange={handleInputChange} />
        </div>
        <UploadDocument
          files={agFinancingFilesToFiles(allFiles)}
          onRemoveFile={addFileToRemove}
          onNewFiles={addNewFiles}
          progress={progress}
          replaceItem={fileReplacer}
          onUpdateFile={onUpdateFile}
        />
      </DialogContent>
      <ModalActionButtons
        closeModal={closeModal}
        disabled={allFiles.length > 0 || debts.length > 0 || note.length > 0 || evaluation || option ? false : true}
        save={updatePhaseInfo}
        changeButtonName={i18next.t('saveChanges')}
      />
    </EnhancedDialog>
  );
};
